import { useEffect, useState } from 'react';

export interface UseSubmitStateOptions {
  /** True while the underlying mutation is pending. */
  isSubmitting: boolean;
  /** Default button label (e.g. "Sign in"). */
  baseLabel: string;
  /** Loading label (e.g. "Signing in…"). Defaults to "Working…". */
  loadingLabel?: string;
  /** ms before the spinner becomes visible. Default 200. */
  spinnerDelayMs?: number;
  /** ms before showing "Still working…" hint. Default 3000. */
  stillWorkingDelayMs?: number;
  /** Optional "Still working…" copy override. */
  stillWorkingLabel?: string;
  /** ms before offering a cancel option. Default 8000. Pass Infinity to disable. */
  offerCancelDelayMs?: number;
}

export interface UseSubmitStateResult {
  /** Label to display on the submit button. */
  buttonLabel: string;
  /** Whether the spinner should be visible (post-200ms delay). */
  isSpinnerVisible: boolean;
  /** Whether to show the "Still working…" hint. */
  showStillWorking: boolean;
  /** Whether to offer a cancel button. */
  offerCancel: boolean;
  /** aria-busy value to pass to the submit button. */
  ariaBusy: boolean;
}

const DEFAULT_LOADING_LABEL = 'Working…';
const DEFAULT_STILL_WORKING_LABEL = 'Still working…';
const DEFAULT_SPINNER_DELAY = 200;
const DEFAULT_STILL_WORKING_DELAY = 3000;
const DEFAULT_OFFER_CANCEL_DELAY = 8000;

export function useSubmitState({
  isSubmitting,
  baseLabel,
  loadingLabel = DEFAULT_LOADING_LABEL,
  spinnerDelayMs = DEFAULT_SPINNER_DELAY,
  stillWorkingDelayMs = DEFAULT_STILL_WORKING_DELAY,
  stillWorkingLabel = DEFAULT_STILL_WORKING_LABEL,
  offerCancelDelayMs = DEFAULT_OFFER_CANCEL_DELAY,
}: UseSubmitStateOptions): UseSubmitStateResult {
  const [isSpinnerVisible, setSpinnerVisible] = useState(false);
  const [showStillWorking, setShowStillWorking] = useState(false);
  const [offerCancel, setOfferCancel] = useState(false);

  useEffect(() => {
    if (!isSubmitting) {
      setSpinnerVisible(false);
      setShowStillWorking(false);
      setOfferCancel(false);
      return;
    }

    const spinnerTimer = setTimeout(() => {
      setSpinnerVisible(true);
    }, spinnerDelayMs);

    const stillWorkingTimer = setTimeout(() => {
      setShowStillWorking(true);
    }, stillWorkingDelayMs);

    const offerCancelTimer = Number.isFinite(offerCancelDelayMs)
      ? setTimeout(() => {
          setOfferCancel(true);
        }, offerCancelDelayMs)
      : null;

    return () => {
      clearTimeout(spinnerTimer);
      clearTimeout(stillWorkingTimer);
      if (offerCancelTimer) clearTimeout(offerCancelTimer);
    };
  }, [isSubmitting, spinnerDelayMs, stillWorkingDelayMs, offerCancelDelayMs]);

  let buttonLabel: string;
  if (!isSubmitting) {
    buttonLabel = baseLabel;
  } else if (showStillWorking) {
    buttonLabel = stillWorkingLabel;
  } else if (isSpinnerVisible) {
    buttonLabel = loadingLabel;
  } else {
    buttonLabel = baseLabel;
  }

  return {
    buttonLabel,
    isSpinnerVisible,
    showStillWorking,
    offerCancel,
    ariaBusy: isSubmitting,
  };
}

export default useSubmitState;
